import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api';
import type {
  AlbumShareListResponse,
  AlbumShareWithUrl,
  CreateAlbumShareRequest,
  UpdateAlbumShareRequest,
} from '@omokage/shared';

/**
 * Query key factory for album share queries
 */
export const albumShareKeys = {
  all: ['albumShares'] as const,
  lists: () => [...albumShareKeys.all, 'list'] as const,
  list: (albumId: string) => [...albumShareKeys.lists(), albumId] as const,
};

/**
 * Hook to fetch all share links for a specific album
 *
 * @param albumId - The album ID to fetch shares for
 * @returns Query result with array of album shares
 */
export function useAlbumShares(albumId: string) {
  return useQuery({
    queryKey: albumShareKeys.list(albumId),
    queryFn: async () => {
      const response = await apiClient.get<AlbumShareListResponse>(
        `/album-shares?albumId=${albumId}`
      );
      return response.shares;
    },
    enabled: !!albumId,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new album share link
 *
 * @returns Mutation function to create a share
 */
export function useCreateAlbumShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: CreateAlbumShareRequest) => {
      const response = await apiClient.post<AlbumShareWithUrl>('/album-shares', data);
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate the shares list for this album
      queryClient.invalidateQueries({
        queryKey: albumShareKeys.list(variables.albumId),
      });
    },
  });
}

/**
 * Hook to delete an album share link
 *
 * @returns Mutation function to delete a share
 */
export function useDeleteAlbumShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ shareId }: { shareId: string; albumId: string }) => {
      await apiClient.delete<{ success: true; message: string }>(`/album-shares/${shareId}`);
    },
    onSuccess: (_, variables) => {
      // Invalidate the shares list for this album
      queryClient.invalidateQueries({
        queryKey: albumShareKeys.list(variables.albumId),
      });
    },
  });
}

/**
 * Hook to update an album share (e.g., set custom slug)
 *
 * @returns Mutation function to update a share
 */
export function useUpdateAlbumShare() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shareId,
      data,
    }: {
      shareId: string;
      albumId: string;
      data: UpdateAlbumShareRequest;
    }) => {
      const response = await apiClient.patch<AlbumShareWithUrl>(`/album-shares/${shareId}`, data);
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate the shares list for this album
      queryClient.invalidateQueries({
        queryKey: albumShareKeys.list(variables.albumId),
      });
    },
  });
}

/**
 * Copy album share URL to clipboard
 *
 * @param shareUrl - The full share URL to copy
 * @returns Promise that resolves to true if successful
 */
export async function copyAlbumShareUrl(shareUrl: string): Promise<boolean> {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      await navigator.clipboard.writeText(shareUrl);
      return true;
    }

    // Fallback for older browsers or non-HTTPS contexts
    const textArea = document.createElement('textarea');
    textArea.value = shareUrl;
    textArea.style.position = 'fixed';
    textArea.style.left = '-999999px';
    textArea.style.top = '-999999px';
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    const successful = document.execCommand('copy');
    document.body.removeChild(textArea);
    return successful;
  } catch {
    return false;
  }
}
